import React, { FC, useCallback } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { AmountInputProps } from '../../../../types';
import { ReduxState, ThunkDispatchType } from 'app/redux/types';

import { useTranslation } from 'app/lang';

import { RemovePledgeStake, removePledgeStake } from '../../../../actions/paymentOptions';
import { AccountType, SubmissionState } from '../../../../constants';
import { selectSubmission } from '../../../../selectors/submission';
import { PledgeStakeDAO } from '../../../../types/backend/paymentOptions';
import AmountInput from '../AmountInput';
import SelectedPaymentOption from '../SelectedPaymentOption';
import PledgeStakeDropdown from './PledgeStakeDropdown';

import dictionary from './PledgeStakeAccount.dictionary.json';

import styles from './PledgeStakeAccount.module.scss';

// ---

type StoreProps = {
  disabled: boolean;
};

type DispatchProp = {
  dispatch: ThunkDispatchType<RemovePledgeStake>;
};

type PledgeStakeProps = {
  betId?: number;
  value?: PledgeStakeDAO | null;
  minValue?: number;
  error?: boolean;
  placeholder: string;
  inputProps: AmountInputProps;
};

const PledgeStake: FC<PledgeStakeProps & DispatchProp & StoreProps> = ({
  betId,
  value,
  minValue,
  inputProps,
  error,
  placeholder,
  disabled,
  dispatch,
}) => {
  const { t } = useTranslation();
  const removePledgeStakeHandler = useCallback(() => {
    if (!betId) {
      return;
    }

    const { onChange, name } = inputProps;

    onChange({ value: '', name, account: AccountType.MAIN_ACCOUNT });
    dispatch(removePledgeStake(betId));
  }, [betId, inputProps]);

  const _className = classNames(styles.inputContainer, {
    [styles.disabled]: disabled,
    [styles.error]: error,
  });

  return (
    <div data-test="pledgeStakeAccount-container">
      <SelectedPaymentOption label={t(dictionary.label)}
                             onClick={removePledgeStakeHandler} />
      <PledgeStakeDropdown betId={betId}
                           activePledgeStake={value}
                           minValue={minValue}
                           className={styles.pledgeStakeDropdown} />
      <div className={_className}
           data-test="pledgeStakeAccount-inputContainer">
        <AmountInput name={inputProps.name}
                     betId={betId}
                     value={inputProps.value}
                     data-test="pledgeStakeAccount-input"
                     inputClassName={styles.input}
                     unitsClassName={styles.units}
                     placeholder={placeholder}
                     onChange={inputProps.onChange}
                     account={AccountType.PLEDGE_STAKE_ACCOUNT} />
      </div>
    </div>
  );
};


const mapStateToProps = (state: ReduxState): StoreProps => ({
  disabled: selectSubmission(state) !== SubmissionState.AWAITING,
});

export default connect(mapStateToProps)(PledgeStake);
